/** @format */

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button } from '../button';
import { useDispatch } from 'src/store';
import type { UnknownAction } from '@reduxjs/toolkit';

type UserRatingProps = {
  nid?: string | number;
  clearError?: () => UnknownAction;
  rateDetail?: (nid: string | number, rating: number) => UnknownAction;
  loading?: boolean;
  ratingError?: string | null;
  ratingSuccess?: boolean;
  isNews?: boolean;
};

export const UserRating = (props: UserRatingProps) => {
  const { nid, clearError, rateDetail, loading, ratingError, ratingSuccess } = props;

  const dispatch = useDispatch();
  const [hovered, setHovered] = useState<number | null>(null);
  const [isVoteSuccess, setIsVoteSuccess] = useState(false);
  const levels = ['Very Bad', 'Bad', 'Okay', 'Good', 'Excellent'];

  useEffect(() => {
    setIsVoteSuccess(Boolean(ratingSuccess));
  }, [ratingSuccess]);

  const handleOnRating = async (rating: number) => {
    if (rateDetail) {
      dispatch(rateDetail(nid, rating) as never);
    }
  };

  const closeModal = () => {
    setIsVoteSuccess(false);
    if (clearError) {
      dispatch(clearError() as never);
    }
  };

  return (
    <>
      <SuccessModal open={isVoteSuccess} closeModal={closeModal} />
      <ErrorModal errorMessage={ratingError} closeModal={closeModal} />
      <Grid className="archive-page-user-rating">
        {levels.map((_label, index) => {
          const starValue = index + 1;

          return (
            <button
              key={starValue}
              disabled={loading}
              className={`archive-page-user-rating-start ${starValue <= (hovered ?? 0) ? 'hovered' : ''}`}
              onClick={() => handleOnRating(starValue)}
              onMouseEnter={() => setHovered(starValue)}
              onMouseLeave={() => setHovered(null)}></button>
          );
        })}
      </Grid>
    </>
  );
};

type ModalProps = {
  open?: boolean;
  closeModal?: () => void;
  errorMessage?: string | null;
};

const SuccessModal = ({ open, closeModal }: ModalProps) => {
  const { t } = useTranslation();
  return (
    <Dialog
      maxWidth="md"
      open={Boolean(open)}
      PaperProps={{
        style: {
          backgroundColor: '#fff',
          borderRadius: 10
        }
      }}
      onClose={closeModal}
      aria-labelledby="max-width-dialog-title"
      className="knowledge-base-user-rating-status-modal">
      <DialogContent>
        <div className="status-icon">
          <svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M48 8C25.92 8 8 25.92 8 48C8 70.08 25.92 88 48 88C70.08 88 88 70.08 88 48C88 25.92 70.08 8 48 8ZM48 80C30.36 80 16 65.64 16 48C16 30.36 30.36 16 48 16C65.64 16 80 30.36 80 48C80 65.64 65.64 80 48 80ZM63.52 33.16L40 56.68L32.48 49.16C30.92 47.6 28.4 47.6 26.84 49.16C25.28 50.72 25.28 53.24 26.84 54.8L37.2 65.16C38.76 66.72 41.28 66.72 42.84 65.16L69.2 38.8C70.76 37.24 70.76 34.72 69.2 33.16C67.64 31.6 65.08 31.6 63.52 33.16Z"
              fill="#08C476"
            />
          </svg>
        </div>
        <div className="status-heading">{t('common.successSubmitted')}</div>
        <div className="status-btn">
          <Button noArrow borderOnly onClick={closeModal} label={t('common.button.closeWindow')} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ErrorModal = ({ errorMessage, closeModal }: ModalProps) => {
  const { t } = useTranslation();
  return (
    <Dialog
      maxWidth="md"
      open={Boolean(errorMessage)}
      PaperProps={{
        style: {
          backgroundColor: '#fff',
          borderRadius: 10
        }
      }}
      onClose={closeModal}
      aria-labelledby="max-width-dialog-title"
      className="knowledge-base-user-rating-status-modal">
      <DialogContent>
        <div className="status-icon">
          <svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M48 28C50.2 28 52 29.8 52 32V48C52 50.2 50.2 52 48 52C45.8 52 44 50.2 44 48V32C44 29.8 45.8 28 48 28ZM47.96 8C25.88 8 8 25.92 8 48C8 70.08 25.88 88 47.96 88C70.08 88 88 70.08 88 48C88 25.92 70.08 8 47.96 8ZM48 80C30.32 80 16 65.68 16 48C16 30.32 30.32 16 48 16C65.68 16 80 30.32 80 48C80 65.68 65.68 80 48 80ZM52 68H44V60H52V68Z"
              fill="#DB4F60"
            />
          </svg>
        </div>
        <div className="status-heading">{errorMessage}</div>
        <div className="status-btn">
          <Button noArrow borderOnly onClick={closeModal} label={t('common.button.closeWindow')} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
